import {
  Box,
  chakra,
  ListItem,
  OrderedList,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spacer,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { CTAButton } from '@gamma/ui/buttons';
import { Input } from '@gamma/ui/form-fields';
import { ErrorBoundary } from '@gamma/ui/overlay';
import {
  CardPageBody,
  CardPageCTA,
  CardPageIllustration,
} from '@gamma/ui/pages';
import { LoadingSpinner } from '@gamma/ui/progress';
import { formatOTPCode } from '@gamma/ui/util';
import { ROUTES } from '@investigator/shared/constants';
import {
  useLogOperationCall,
  useMfaSessionValidation,
  useOtpQrCode,
  useTokenManagement,
} from '@investigator/shared/hooks';
import { i18n } from '@investigator/shared/localization';
import {
  AuthContext,
  getUserId,
  getUserScopedKey,
} from '@investigator/shared/providers';
import { chunkSubstring, setRole } from '@investigator/shared/utils';
import { getMfaErrorType } from '@investigator/shared/utils/getMfaErrorType';
import { Auth } from 'aws-amplify';
import { ChangeEvent, useContext, useState, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

const pageContent = i18n.pages.registration.verifyOtp;

interface OTPFormData {
  OTPCode: string;
}

const NeedAppTooltipContent = () => {
  const { title, instructions } = pageContent.tooltips.app;
  return (
    <Stack>
      <Text textStyle="body-md">{title}</Text>
      <Spacer />
      <Text textStyle="body-md">{instructions.title}</Text>
      <Box>
        <OrderedList spacing={1}>
          <ListItem textStyle="body-md">{instructions.step1}</ListItem>
          <ListItem textStyle="body-md">{instructions.step2}</ListItem>
          <ListItem textStyle="body-md">
            {i18n.formatString(instructions.step3.body, {
              hint: (
                <chakra.span color="gray.500">
                  {instructions.step3.hint}
                </chakra.span>
              ),
            })}
          </ListItem>
        </OrderedList>
      </Box>
    </Stack>
  );
};

const CantScanTooltipContent = ({
  otpClientSecret,
}: {
  otpClientSecret: string;
}) => {
  const { title, instructions } = pageContent.tooltips.barcode;
  return (
    <Popover trigger="hover">
      <PopoverTrigger>
        <Text
          color="blue.400"
          textDecoration="underline"
          as="span"
          tabIndex={0}
        >
          {title}
        </Text>
      </PopoverTrigger>
      <PopoverContent sx={{ width: '400px' }}>
        <PopoverBody>
          <Stack>
            <Text textStyle="body-md">{title}</Text>
            <Spacer />
            <Box>
              <OrderedList spacing={1}>
                <ListItem textStyle="body-md">{instructions.step1}</ListItem>
                <ListItem textStyle="body-md">{instructions.step2}</ListItem>
                <ListItem textStyle="body-md">
                  {i18n.formatString(instructions.step3.body, {
                    key: (
                      <chakra.span color="gray.500">
                        {chunkSubstring(otpClientSecret)}
                      </chakra.span>
                    ),
                  })}
                </ListItem>
                <ListItem textStyle="body-md">{instructions.step4}</ListItem>
                <ListItem textStyle="body-md">{instructions.step5}</ListItem>
              </OrderedList>
            </Box>
          </Stack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export const ConfirmOTPCode = () => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    setError,
    setValue,
    setFocus,
    formState: { errors, isDirty },
  } = useForm<OTPFormData>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { user, setUser, setUserRole, setAuthState } = useContext(AuthContext);
  const logOperationCall = useLogOperationCall();
  const { code, loadingQrCode } = useOtpQrCode(
    user
      ? {
          ...user,
          challengeName: user.challengeName ?? '',
          challengeParam: user.challengeParam ?? {},
        }
      : undefined,
  );

  const { saveIdToken, clearTokens } = useTokenManagement();

  // Validate MFA user session
  useMfaSessionValidation(user);

  const handleOTPInputChange = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    setValue('OTPCode', formatOTPCode(value));
  };

  const handleSubmission: SubmitHandler<OTPFormData> = async ({ OTPCode }) => {
    setIsLoading(true);
    try {
      // Ensure user session is still valid before attempting MFA setup
      if (!user) {
        navigate('/login?error=mfa_session_expired');
        return;
      }

      // When Auth.verifyTotpToken() fails (e.g., wrong code), AWS Amplify clears its
      // internal user cache, causing subsequent Auth.currentAuthenticatedUser() calls
      // to fail with "The user is not authenticated". The context user still has the
      // valid session info needed for retry, so we fall back to it.
      let currentUser;
      try {
        currentUser = await Auth.currentAuthenticatedUser();
      } catch {
        currentUser = user;
      }

      await Auth.verifyTotpToken(currentUser, OTPCode.replace(' ', ''));

      await Auth.setPreferredMFA(currentUser, 'SOFTWARE_TOKEN_MFA');

      try {
        await Auth.currentSession();
        const updatedUser = await Auth.currentAuthenticatedUser({
          bypassCache: true,
        });

        if (updatedUser) {
          setUser?.(updatedUser);
          setRole({ user: updatedUser, setUserRole });

          const idToken = updatedUser?.signInUserSession?.idToken?.jwtToken;
          if (idToken) {
            saveIdToken(idToken, updatedUser);
          } else {
            console.warn('[ConfirmOTPCode] No ID token found after MFA setup');
          }
        }

        try {
          await logOperationCall('MFA_REGISTERED', 'USER');
        } catch (e) {
          console.error('[ConfirmOTPCode] logOperationCall error:', e);
        }

        // Set flags for terms acceptance after login with user scoping
        const userId = getUserId(updatedUser);
        if (userId) {
          localStorage.setItem(
            getUserScopedKey('humio_tc_accepted', userId),
            'true',
          );
        }
        // Also set legacy flag for backward compatibility
        localStorage.setItem('humio_tc_accepted', 'true');

        try {
          await Auth.signOut();
        } catch (e) {
          console.error('[ConfirmOTPCode] signOut error:', e);
        } finally {
          // Use token management hook for cleanup
          clearTokens(updatedUser);

          setUser?.(undefined);
          setUserRole?.(undefined);
          setAuthState?.(undefined);

          setIsLoading(false);
          navigate(ROUTES.login, { replace: true });
        }
      } catch (sessionError) {
        console.error('[ConfirmOTPCode] session refresh error:', sessionError);
        throw sessionError;
      }
    } catch (e) {
      const { errorType } = getMfaErrorType(e);
      switch (errorType) {
        case 'session':
          navigate('/login?error=mfa_session_expired');
          break;
        case 'otp':
          setError('OTPCode', { message: i18n.forms.validation.otp });
          setIsLoading(false);
          break;
        case 'generic':
          clearTokens(
            user
              ? {
                  ...user,
                  challengeName: user.challengeName ?? '',
                  challengeParam: user.challengeParam ?? {},
                }
              : undefined,
          );
          setError('OTPCode', { message: i18n.forms.validation.otp });
          setIsLoading(false);
          break;
      }
    }
  };

  useEffect(() => {
    setFocus('OTPCode');
  }, [setFocus]);

  const { cta, instructions } = pageContent;

  return (
    <ErrorBoundary page="Registration MFA Confirm OTP Page">
      {loadingQrCode ? (
        <LoadingSpinner />
      ) : (
        <CardPageIllustration qrCodeLink={code.qrCode} />
      )}
      <CardPageBody>
        <OrderedList mb={4}>
          <ListItem fontSize="xs">
            {i18n.formatString(
              instructions.step1.body,
              <strong>{i18n.dictionary.open}</strong>,
              <Tooltip label={<NeedAppTooltipContent />}>
                <Text
                  color="blue.400"
                  textDecoration="underline"
                  as="span"
                  tabIndex={0}
                >
                  {instructions.step1.help}
                </Text>
              </Tooltip>,
            )}
          </ListItem>
          <ListItem fontSize="xs">
            {i18n.formatString(
              instructions.step2.body,
              <strong>{i18n.dictionary.add}</strong>,
              <CantScanTooltipContent otpClientSecret={code.qrSecret} />,
            )}
          </ListItem>
          <ListItem fontSize="xs">
            {i18n.formatString(
              instructions.step3.body,
              <strong>{instructions.step3.start}</strong>,
            )}
          </ListItem>
        </OrderedList>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <form onSubmit={handleSubmit(handleSubmission)} id="confirmOTPCode">
            <Input
              label={i18n.forms.labels.enterOTP}
              autoComplete="one-time-code"
              placeholder="xxx xxx"
              isLabelHidden
              {...register('OTPCode', {
                onChange: handleOTPInputChange,
              })}
              error={errors?.OTPCode?.message}
            />
          </form>
        )}
      </CardPageBody>
      <CardPageCTA>
        <CTAButton
          type="submit"
          form="confirmOTPCode"
          disabled={!isDirty}
          isLoading={isLoading}
        >
          {cta}
        </CTAButton>
      </CardPageCTA>
    </ErrorBoundary>
  );
};
